import React, { useEffect, useMemo, useState } from 'react';
import api from '@/lib/axios';
import { formatScheduleLabel } from '@/lib/utils';
import { subjectBaseName, AREAL_OPTIONS, GKZ_OPTIONS } from '../utils';
import { getFooterSettings, footerToParams } from '@/components/FooterDateBar';
import { matchesFreeText } from '@/components/search/SearchBuilder';

const WEEKDAYS = [
  { value: 'Mo', label: 'Mo', subtitle: 'Montag' },
  { value: 'Di', label: 'Di', subtitle: 'Dienstag' },
  { value: 'Mi', label: 'Mi', subtitle: 'Mittwoch' },
  { value: 'Do', label: 'Do', subtitle: 'Donnerstag' },
  { value: 'Fr', label: 'Fr', subtitle: 'Freitag' },
  { value: 'Sa', label: 'Sa', subtitle: 'Samstag' },
  { value: 'So', label: 'So', subtitle: 'Sonntag' },
];

const asItems = (rows, mapFn) => {
  const seen = new Set();
  const out = [];
  for (const row of rows || []) {
    const item = mapFn(row);
    if (!item?.value) continue;
    const key = String(item.value).toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
};

const byDeLabel = (a, b) =>
  String(a.label).localeCompare(String(b.label), 'de', { sensitivity: 'base' });

/** Lokale Kataloge: alle Treffer (Dropdown scrollt); Remote bleibt begrenzt. */
const LOCAL_LIMIT = 500;

/** Lokale Kataloge + Remote-APIs → Vorschlagsliste für ein Suchfeld. */
export async function fetchFieldSuggestions(fieldKey, query, catalogs = {}) {
  const q = String(query || '').trim();
  const matchOrAll = (hay) => matchesFreeText(String(hay || ''), q);

  const { tariffs = [], subjects = [], teachers = [], locations = [], rooms = [] } = catalogs;

  switch (fieldKey) {
    case 'q_unterrichtsform':
      return asItems(
        tariffs.filter((t) => matchOrAll(t.label)),
        (t) => ({ value: t.label, label: t.label, subtitle: 'Tarif' }),
      ).sort(byDeLabel).slice(0, LOCAL_LIMIT);

    case 'q_fach': {
      // Immer zuerst Basis-Fach ohne Tarif („Keyboard“ statt „Keyboard Einzel 30 Min“)
      const bases = asItems(
        subjects
          .map((s) => ({ label: subjectBaseName(s.label) }))
          .filter((s) => matchOrAll(s.label)),
        (s) => ({ value: s.label, label: s.label, subtitle: 'Fach' }),
      ).sort(byDeLabel);

      // Ohne Eingabe: alle Basis-Fächer (nicht nur A/B durch 20er-Cap)
      if (!q) return bases;

      // Beim Tippen: Basis zuerst, danach konkrete Fach+Form-Varianten
      const variants = asItems(
        subjects.filter((s) => {
          const base = subjectBaseName(s.label);
          return base && base !== s.label && matchOrAll(s.label);
        }),
        (s) => ({ value: s.label, label: s.label, subtitle: 'Fach + Form' }),
      ).sort(byDeLabel);

      const baseKeys = new Set(bases.map((b) => b.value.toLowerCase()));
      const extra = variants.filter((v) => !baseKeys.has(v.value.toLowerCase()));
      return [...bases, ...extra].slice(0, LOCAL_LIMIT);
    }

    case 'q_lehrkraft':
      return asItems(
        teachers.filter((t) => matchOrAll(t.label) || matchOrAll(t.searchText) || matchOrAll(t.suggestLabel)),
        (t) => ({
          value: t.suggestLabel || t.label,
          label: t.suggestLabel || t.label,
          subtitle: 'Lehrkraft',
        }),
      ).sort(byDeLabel).slice(0, LOCAL_LIMIT);

    case 'q_raum':
      return asItems(
        rooms.filter((r) => matchOrAll(r.label) || matchOrAll(r.location)),
        (r) => ({
          value: r.label,
          label: r.label,
          subtitle: r.location ? `Raum · ${r.location}` : 'Raum',
        }),
      ).sort(byDeLabel).slice(0, LOCAL_LIMIT);

    case 'q_standort':
      return asItems(
        locations.filter((l) => matchOrAll(l.label) || matchOrAll(l.searchText)),
        (l) => ({
          value: l.label,
          label: l.label,
          subtitle: l.searchText && l.searchText !== l.label
            ? `Standort · ${l.searchText}`
            : 'Standort / Unterrichtsort',
        }),
      ).sort(byDeLabel).slice(0, LOCAL_LIMIT);

    case 'q_areal':
    case 'q_payer_areal':
      return asItems(
        AREAL_OPTIONS.filter((o) => matchOrAll(o.label) || matchOrAll(o.value)),
        (o) => ({ value: o.value, label: o.label, subtitle: 'Areal / Region' }),
      );

    case 'q_gkz':
      return asItems(
        GKZ_OPTIONS.filter((o) => matchOrAll(o.label) || matchOrAll(o.value)),
        (o) => ({ value: o.value, label: o.label, subtitle: 'GKZ / Filiale' }),
      );

    case 'q_termin': {
      const hits = WEEKDAYS.filter(
        (d) => !q
          || matchOrAll(d.label)
          || matchOrAll(d.subtitle)
          || d.subtitle.toLowerCase().startsWith(qLower),
      );
      return hits.map((d) => ({ value: d.value, label: d.label, subtitle: d.subtitle }));
    }

    case 'q_dauer': {
      const digits = q.replace(/\D/g, '');
      const fromTariffs = tariffs.map((t) => t.duration_minutes).filter((n) => n != null);
      const extras = [15, 20, 30, 45, 60, 90];
      const uniq = [...new Set([...fromTariffs, ...extras].map(Number))]
        .filter((n) => !Number.isNaN(n) && (!digits || String(n).includes(digits)))
        .sort((a, b) => a - b);
      return asItems(uniq, (n) => ({
        value: String(n),
        label: `${n} Min.`,
        subtitle: 'Dauer',
      })).slice(0, 12);
    }

    case 'q_bezeichnung': {
      const places = q.length < 2 ? [] : asItems(
        locations.filter((l) => matchOrAll(l.label) || matchOrAll(l.searchText)),
        (l) => ({ value: l.label, label: l.label, subtitle: 'Schule / Standort' }),
      ).sort(byDeLabel);
      if (q.length < 2) return [];
      try {
        const footer = footerToParams(getFooterSettings());
        const r = await api.get('/enrollments', {
          params: { q_bezeichnung: q, page: 1, per_page: 40, ...footer },
        });
        const rows = r.data?.items || [];
        const courses = asItems(rows, (e) => {
          const name = e.course?.name || e.course_name || e.course_data?.name;
          if (!name) return null;
          const label = formatScheduleLabel(name) || name;
          return {
            value: name,
            label,
            subtitle: [e.student?.last_name, e.student?.first_name].filter(Boolean).join(', ') || 'Veranstaltung',
          };
        });
        const seen = new Set(courses.map((item) => item.value.toLowerCase()));
        return [...places.filter((item) => !seen.has(item.value.toLowerCase())), ...courses].slice(0, 20);
      } catch {
        return places.slice(0, 20);
      }
    }

    case 'q_student_name': {
      if (q.length < 2) return [];
      try {
        // Nur Schüler mit Belegung im aktuellen Fußleisten-Filter (Zeitraum / Nur Aktive / Aktiv ab)
        const footer = footerToParams(getFooterSettings());
        const r = await api.get('/enrollments', {
          params: { q_student_name: q, page: 1, per_page: 40, ...footer },
        });
        const rows = r.data?.items || [];
        return asItems(rows, (e) => {
          const s = e.student || {};
          const name = `${s.last_name || ''}, ${s.first_name || ''}`.replace(/^,\s*/, '').trim();
          if (!name) return null;
          return {
            value: name,
            label: name,
            subtitle: formatScheduleLabel(e.course?.name || e.course_name) || e.course?.name || e.course_name || 'Schüler:in',
          };
        }).slice(0, 20);
      } catch {
        return [];
      }
    }

    case 'q_payer_name': {
      if (q.length < 2) return [];
      try {
        const r = await api.get('/guardians', { params: { search: q, limit: 20 } });
        const rows = Array.isArray(r.data) ? r.data : r.data?.items || [];
        return asItems(rows, (g) => ({
          value: `${g.last_name || ''}, ${g.first_name || ''}`.replace(/^,\s*/, '').trim(),
          label: `${g.last_name || ''}, ${g.first_name || ''}`.replace(/^,\s*/, '').trim(),
          subtitle: g.email || g.phone || 'Zahlungspflichtige:r',
        })).slice(0, 20);
      } catch {
        return [];
      }
    }

    default:
      return [];
  }
}

export const SUGGESTABLE_FIELD_KEYS = new Set([
  'q_unterrichtsform',
  'q_fach',
  'q_lehrkraft',
  'q_raum',
  'q_standort',
  'q_areal',
  'q_payer_areal',
  'q_gkz',
  'q_termin',
  'q_dauer',
  'q_bezeichnung',
  'q_student_name',
  'q_payer_name',
]);

/** Debounced suggestions for the currently focused search field. */
export function useFieldSearchSuggestions(fieldKey, query, catalogs, enabled) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [footerTick, setFooterTick] = useState(0);

  const catalogKey = useMemo(
    () => JSON.stringify({
      t: catalogs?.tariffs?.length,
      s: catalogs?.subjects?.length,
      te: catalogs?.teachers?.length,
      l: catalogs?.locations?.length,
      r: catalogs?.rooms?.length,
    }),
    [catalogs],
  );

  useEffect(() => {
    const h = () => setFooterTick((n) => n + 1);
    window.addEventListener('footer-settings-changed', h);
    return () => window.removeEventListener('footer-settings-changed', h);
  }, []);

  useEffect(() => {
    if (!enabled || !fieldKey || !SUGGESTABLE_FIELD_KEYS.has(fieldKey)) {
      setItems([]);
      return undefined;
    }
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const next = await fetchFieldSuggestions(fieldKey, query, catalogs);
        if (!cancelled) setItems(next);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    const t = setTimeout(run, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // catalogs via catalogKey size fingerprint; footerTick = Fußleisten-Filter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldKey, query, enabled, catalogKey, footerTick]);

  return { items, loading };
}

export function FieldSuggestDropdown({
  items,
  loading,
  highlight,
  onSelect,
  onHighlight,
}) {
  if (loading && items.length === 0) {
    return (
      <div
        className="absolute left-0 right-0 top-full mt-0.5 z-[60] bg-white border border-slate-200 rounded-md shadow-lg text-xs text-slate-500 px-3 py-2"
        data-field-suggest-open="1"
        data-testid="field-suggest-dropdown"
      >
        Suche…
      </div>
    );
  }
  if (!items.length) return null;

  return (
    <div
      className="absolute left-0 right-0 top-full mt-0.5 z-[60] bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-y-auto"
      data-field-suggest-open="1"
      data-testid="field-suggest-dropdown"
    >
      <ul>
        {items.map((item, idx) => (
          <li
            key={`${item.value}-${idx}`}
            onMouseDown={(e) => {
              e.preventDefault();
              onSelect(item);
            }}
            onMouseEnter={() => onHighlight?.(idx)}
            className={`px-2.5 py-1.5 cursor-pointer border-b border-slate-100 last:border-0 ${
              idx === highlight ? 'bg-brand-red/10' : 'hover:bg-brand-red/5'
            }`}
            data-testid={`field-suggest-item-${idx}`}
          >
            <div className="text-xs font-medium text-slate-800 leading-tight">{item.label}</div>
            {item.subtitle && (
              <div className="text-[10px] text-slate-500 leading-tight">{item.subtitle}</div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default FieldSuggestDropdown;
